import { useState, useEffect } from 'react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { BookOpen, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import CourseCard from '../dashboard/CourseCard';

export default function FeaturedCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [allCourses, setAllCourses] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [categoriesWithCounts, setCategoriesWithCounts] = useState([]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const q = query(collection(db, 'courses'), where('status', '==', 'published'));
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCourses(data);

        // Calculate categories
        const counts = {};
        data.forEach(c => {
          if (c.category) counts[c.category] = (counts[c.category] || 0) + 1;
        });
        const filterList = Object.entries(counts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        setCategoriesWithCounts(filterList);

        // Initial courses (Top 4)
        setCourses(data.slice(0, 4));
      } catch (err) {
        console.error('Error fetching featured courses:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const filtered = filterCategory === 'all'
    ? allCourses.slice(0, 4)
    : allCourses.filter(c => c.category === filterCategory);

  if (loading) {
    return (
      <section className="py-24 bg-white dark:bg-[#050505] flex justify-center items-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
      </section>
    );
  }

  if (courses.length === 0) return null;

  return (
    <section className="py-16 sm:py-24 relative bg-white dark:bg-[#050505] overflow-hidden" dir="rtl">
      {/* Ambient background styling */}
      <div className="absolute top-0 right-0 w-[200px] sm:w-[300px] lg:w-[400px] h-[200px] sm:h-[300px] lg:h-[400px] bg-primary-500/10 dark:bg-primary-600/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 sm:mb-16 gap-5 sm:gap-6">
          <div className="text-center md:text-right">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 font-bold mb-3 sm:mb-4 border border-primary-100 dark:border-primary-500/20 text-sm sm:text-base">
              <BookOpen className="w-4 h-4" /> الأحدث لدينا
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-3 sm:mb-4 tracking-tight">
              دورات <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-amber-500">مميزة</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-500 dark:text-zinc-400 font-medium max-w-xl px-2 sm:px-0">
              تصفح أحدث الدورات التي انضمت لمنصتنا وابدأ رحلة تعليمية جديدة اليوم بصحبة نخبة من الخبراء.
            </p>
          </div>

          <Link
            to="/courses"
            className="group flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white font-bold hover:bg-primary-50 dark:hover:bg-primary-500/10 hover:text-primary-600 dark:hover:text-primary-400 border border-transparent hover:border-primary-200 dark:hover:border-primary-500/20 transition-all shadow-sm"
          >
            تصفح كل الدورات
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Categories Pills - Cinematic Style */}
        <div className="flex flex-col items-center gap-6 mb-12 sm:mb-16">
          <h3 className="text-3xl font-bold text-slate-900 dark:text-white relative">
            الأقســــام
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary-500 rounded-full"></div>
          </h3>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-md transition-all duration-300  cursor-pointer  ${filterCategory === 'all'
                ? 'bg-primary-600 text-white border-primary-500 shadow-lg shadow-primary-500/30'
                : 'bg-slate-200 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400  dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-500/30'
                }`}
            >
              <span className='text-lg'>الكــــل</span> <span className='text-md'>({allCourses.length})</span>
            </button>
            {categoriesWithCounts.map(cat => (
              <button
                key={cat.name}
                onClick={() => setFilterCategory(cat.name)}
                className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-md transition-all duration-300  ${filterCategory === cat.name
                  ? 'bg-primary-600 text-white border-primary-500 shadow-lg shadow-primary-500/30'
                  : 'bg-slate-200 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400  dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-500/30'
                  }`}
              >
                <span className='text-lg'>{cat.name}</span> <span className='text-md'>({cat.count})</span>

              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 auto-rows-fr">
          {filtered.map(course => (
            <CourseCard
              key={course.id}
              course={course}
              isEnrolled={false}
              isPublicView={true}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
